import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const DERIVED_FIELDS = new Set(["weight_lbs", "length_in", "width_in", "height_in", "cft"]);

const formatTimestamp = (value) => {
  if (!value) return "";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${String(value).replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const truncateText = (value, max = 120) => {
  if (!value) return "";
  const text = String(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3)}...`;
};

export default function AdminProductSuggestions() {
  const me = getCurrentUser();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionId, setActionId] = useState(null);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sortKey, setSortKey] = useState("last_seen_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const selectAllRef = useRef(null);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      if (search.trim()) params.set("search", search.trim());
      const resp = await fetch(`${API_BASE}/api/admin/mrp-product-suggestions?${params.toString()}`, {
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Fetch failed");
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number.isFinite(data.total) ? data.total : 0);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load product suggestions.");
    } finally {
      setLoading(false);
    }
  }, [me?.email, me?.role, status, limit, offset, search]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleAction = async (rowId, action) => {
    if (!rowId || !action) return;
    setActionId(rowId);
    setError("");
    setActionMessage("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const resp = await fetch(
        `${API_BASE}/api/admin/mrp-product-suggestions/${rowId}/${action}`,
        { method: "POST", headers }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Update failed");
      }
      setActionMessage(action === "approve" ? "Suggestion approved." : "Suggestion rejected.");
      await loadSuggestions();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to update suggestion.");
    } finally {
      setActionId(null);
    }
  };

  const displayRows = useMemo(
    () => items.filter((row) => !DERIVED_FIELDS.has(row.field_name || "")),
    [items]
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const allowed = new Set(displayRows.map((row) => row.id));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next;
    });
  }, [displayRows]);

  const sortedRows = useMemo(() => {
    const rows = [...displayRows];
    const direction = sortDirection === "desc" ? -1 : 1;
    const getValue = (row) => {
      switch (sortKey) {
        case "cust_code":
        case "product_code":
        case "supplier_code":
        case "field_name":
          return String(row[sortKey] || "").toLowerCase();
        case "old_value":
        case "new_value":
          return String(row[sortKey] || "").toLowerCase();
        case "last_seen_at": {
          const dt = new Date(row.last_seen_at || 0);
          return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
        }
        default:
          return row.id || 0;
      }
    };
    rows.sort((a, b) => {
      const left = getValue(a);
      const right = getValue(b);
      if (left === right) return 0;
      return left > right ? direction : -direction;
    });
    return rows;
  }, [displayRows, sortKey, sortDirection]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "last_seen_at" ? "desc" : "asc");
  };

  const allVisibleSelected = sortedRows.length > 0 && selectedIds.size === sortedRows.length;
  const someVisibleSelected = selectedIds.size > 0 && selectedIds.size < sortedRows.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const handleToggleAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sortedRows.map((row) => row.id)));
  };

  const handleToggleRow = (rowId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleBulkApply = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(
      `Apply ${bulkAction === "approve" ? "Approve" : "Reject"} to ${ids.length} selected suggestions?`
    );
    if (!confirmed) return;
    setBulkMessage("");
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const resp = await fetch(`${API_BASE}/api/admin/mrp/product-suggestions/bulk`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: bulkAction, ids }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Bulk update failed");
      }
      const processed = Number.isFinite(data.processed) ? data.processed : ids.length;
      setBulkMessage(
        bulkAction === "approve"
          ? `Approved ${processed} suggestions.`
          : `Rejected ${processed} suggestions.`
      );
      setSelectedIds(new Set());
      await loadSuggestions();
    } catch (err) {
      console.error(err);
      setError(err.message || "Bulk update failed.");
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">Product Suggestions</h1>
          <p className="text-sm text-slate-600">
            Review vessel-derived product changes before applying them to MRP Products.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {["PENDING", "APPROVED", "REJECTED", "ALL"].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            placeholder="Search cust or product code"
            className="w-56 rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[50, 100, 200, 500].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadSuggestions}
            disabled={loading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="text-sm text-slate-600">
        Total suggestions: <span className="font-semibold text-slate-800">{total}</span>
      </div>

      {actionMessage && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {actionMessage}
        </div>
      )}

      {bulkMessage && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {bulkMessage}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
        <div>Selected: {selectedIds.size}</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Bulk Action</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </select>
          <button
            type="button"
            onClick={handleBulkApply}
            disabled={selectedIds.size === 0 || !bulkAction}
            className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Apply to Selected
          </button>
        </div>
      </div>

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleToggleAll}
                />
              </th>
              {[
                { key: "cust_code", label: "Cust Code" },
                { key: "product_code", label: "Product Code" },
                { key: "supplier_code", label: "Supplier Code" },
                { key: "field_name", label: "Field" },
                { key: "old_value", label: "Old Value" },
                { key: "new_value", label: "New Value" },
                { key: "last_seen_at", label: "Last Seen" },
                { key: "actions", label: "Actions" },
              ].map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left font-semibold cursor-pointer select-none"
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className="ml-1 text-xs">{sortDirection === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && sortedRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                  Loading suggestions...
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                  No suggestions found.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const isPending = row.status === "PENDING";
                return (
                  <tr key={row.id}>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => handleToggleRow(row.id)}
                      />
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.cust_code || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.product_code || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.supplier_code || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.field_name || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {truncateText(row.old_value)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {truncateText(row.new_value)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {formatTimestamp(row.last_seen_at)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {isPending ? (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleAction(row.id, "approve")}
                            disabled={actionId === row.id}
                            className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                          >
                            {actionId === row.id ? "Working..." : "Approve"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAction(row.id, "reject")}
                            disabled={actionId === row.id}
                            className="rounded border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                          >
                            {actionId === row.id ? "Working..." : "Reject"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">No actions</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
